import { useState } from 'react';
import { X, Lock, Code2, AlignLeft } from 'lucide-react';

import { tryParseDeep } from '../../lib/json-parser';
import type { StoredCaptureEvent } from '../../lib/types';
import { cn } from '../../lib/utils';

import { JsonHighlight } from './json-highlight';

interface DetailDrawerProps {
  event: StoredCaptureEvent;
  startAt: number;
  onClose: () => void;
  projectId: string;
}

function isProtected(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__protected'] === true
  );
}

function eventLabel(capability: string, action: string): string {
  if (capability === 'vm' && action === 'boot') return 'function called';
  if (capability === 'vm' && action === 'return') return 'function returned';
  return `${capability}.${action}`;
}

// ─── Payload viewer ──────────────────────────────────────────────────────────

function PayloadView({ payload }: { payload: unknown }): import('react/jsx-runtime').JSX.Element {
  const { parsed, canBeautified } = tryParseDeep(payload);
  const [pretty, setPretty] = useState(true); // default: show pretty

  // Always show the toggle if payload was a string we could parse
  const showToggle = canBeautified;

  return (
    <div className="space-y-2">
      {showToggle && (
        <div className="flex items-center justify-end">
          <div className="flex items-center bg-white/[0.04] border border-white/8 rounded-lg p-0.5">
            <button
              onClick={(): void => setPretty(true)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all',
                pretty
                  ? 'bg-white/10 text-foreground'
                  : 'text-muted-foreground/40 hover:text-muted-foreground/70',
              )}
            >
              <AlignLeft className="h-3 w-3" />
              Pretty
            </button>
            <button
              onClick={(): void => setPretty(false)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all',
                !pretty
                  ? 'bg-white/10 text-foreground'
                  : 'text-muted-foreground/40 hover:text-muted-foreground/70',
              )}
            >
              <Code2 className="h-3 w-3" />
              Raw
            </button>
          </div>
        </div>
      )}

      {pretty && canBeautified ? (
        <JsonHighlight value={parsed} />
      ) : (
        <pre className="text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed text-muted-foreground/80">
          {typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DetailDrawer({
  event,
  startAt,
  onClose,
  projectId: _projectId,
}: DetailDrawerProps): import('react/jsx-runtime').JSX.Element {
  const offset = Math.round(event.at - startAt);
  const payloadProtected = isProtected(event.payload);
  const reqProtected = Boolean(event.requestPayload) ? isProtected(event.requestPayload) : false;
  const resProtected = Boolean(event.responsePayload) ? isProtected(event.responsePayload) : false;
  const errProtected = Boolean(event.errorPayload) ? isProtected(event.errorPayload) : false;
  const anyProtected = payloadProtected || reqProtected || resProtected || errProtected;
  const phaseColor =
    event.phase === 'error'
      ? 'text-[var(--flux-error)]'
      : event.phase === 'response'
        ? 'text-[var(--flux-success)]/70'
        : 'text-muted-foreground/50';

  return (
    <div className="bg-card/30 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 sticky top-0 bg-[#0a0c11]/95 backdrop-blur z-10">
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className="text-muted-foreground/30">#{event.seq}</span>
          <span className="font-medium text-foreground/80">
            {eventLabel(event.capability, event.action)}
          </span>
          <span className={phaseColor}>{event.phase}</span>
          <span className="text-muted-foreground/30 tabular-nums">+{offset}ms</span>
          {event.durationMs !== undefined && event.durationMs > 0 && (
            <span
              className={cn(
                'tabular-nums font-semibold',
                event.durationMs > 500 ? 'text-[var(--flux-warning)]' : 'text-muted-foreground/40',
              )}
            >
              {event.durationMs}ms
            </span>
          )}
          {anyProtected && (
            <span className="inline-flex items-center gap-1 text-muted-foreground/40">
              <Lock className="h-2.5 w-2.5" />
              <span className="text-[9px]">encrypted</span>
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground/30 hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <>
          {event.phase === 'span' && event.requestPayload !== undefined ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest bg-white/[0.03] px-2 py-0.5 rounded-sm inline-block">
                  Request
                </div>
                {reqProtected && (
                  <p className="text-muted-foreground/50 italic text-xs">Encrypted</p>
                )}
                <PayloadView payload={event.requestPayload} />
              </div>
              {event.responsePayload !== undefined && (
                <div className="space-y-2">
                  <div className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest bg-[var(--flux-success)]/10 text-[var(--flux-success)]/80 px-2 py-0.5 rounded-sm inline-block">
                    Response
                  </div>
                  {resProtected && (
                    <p className="text-muted-foreground/50 italic text-xs">Encrypted</p>
                  )}
                  <PayloadView payload={event.responsePayload} />
                </div>
              )}
              {event.errorPayload !== undefined && (
                <div className="space-y-2">
                  <div className="text-[10px] font-mono text-[var(--flux-error)] uppercase tracking-widest bg-[var(--flux-error)]/10 px-2 py-0.5 rounded-sm inline-block">
                    Error
                  </div>
                  {errProtected && (
                    <p className="text-[var(--flux-error)]/50 italic text-xs">Encrypted</p>
                  )}
                  <PayloadView payload={event.errorPayload} />
                </div>
              )}
            </div>
          ) : (
            <PayloadView payload={event.payload} />
          )}
        </>
      </div>
    </div>
  );
}
